import { useLayoutEffect, useState, useCallback, RefObject } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

interface ISize {
  width: number | null;
  height: number | null;
}

export function useElementSize<TElement extends HTMLElement>(ref: RefObject<TElement | null>) {
  const [size, setSize] = useState<ISize>({ width: null, height: null });

  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    if (!Array.isArray(entries) || entries.length === 0) {
      return;
    }

    const entry = entries[0];
    setSize({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    });
  }, []);

  useLayoutEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new ResizeObserver(entries => handleResize(entries));
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [handleResize, ref]);

  return size;
}

export function useElementSizeCallback() {
  const [size, setSize] = useState<ISize>({ width: null, height: null });
  const [element, setElement] = useState<HTMLElement | null>(null);

  const ref = useCallback((newElement: HTMLElement | null) => {
    setElement(newElement);
  }, []);

  const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
    if (!Array.isArray(entries) || entries.length === 0) {
      return;
    }

    const entry = entries[0];
    setSize({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    });
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(handleResize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleResize, element]);

  return { ref, size };
}
